import { Input } from "@/components/ui/input";
import * as React from "react";
import JsonEditorBase from "./JsonEditorBase";
interface AbiInput {
  name: string;
  type: string;
  internalType?: string;
  components?: AbiInput[]; // For tuples/structs
}
interface ArgsInputProps {
  label: string;
  description?: string;
  value: any; // Array of arguments
  onChange: (value: any) => void;
  abi?: any; // The function ABI containing inputs
}
export default function ArgsInput({
  label,
  description,
  value,
  onChange,
  abi
}: ArgsInputProps) {
  const [isManualMode, setIsManualMode] = React.useState(false);
  const [autoModeValues, setAutoModeValues] = React.useState<Record<number, string>>({});
  const [validationErrors, setValidationErrors] = React.useState<Record<number, string>>({});

  // Parse ABI to get function inputs
  const functionInputs: AbiInput[] = React.useMemo(() => {
    if (!abi) return [];
    try {
      const abiData = typeof abi === "string" ? JSON.parse(abi) : abi;
      return abiData.inputs || [];
    } catch {
      return [];
    }
  }, [abi]);

  // Initialize auto mode values from current value when switching modes or when value changes
  React.useEffect(() => {
    if (!isManualMode && Array.isArray(value) && value.length > 0) {
      const initialValues: Record<number, string> = {};
      value.forEach((val, idx) => {
        initialValues[idx] = typeof val === "string" ? val : JSON.stringify(val);
      });
      setAutoModeValues(initialValues);
    }
  }, [isManualMode, value]);

  // Handle auto input changes and update parent
  const handleAutoInputChange = (index: number, newValue: string, inputType: string) => {
    const updatedValues = {
      ...autoModeValues,
      [index]: newValue
    };
    setAutoModeValues(updatedValues);

    // Validate the input
    const error = validateInput(inputType, newValue);
    const updatedErrors = {
      ...validationErrors
    };
    if (error) {
      updatedErrors[index] = error;
    } else {
      delete updatedErrors[index];
    }
    setValidationErrors(updatedErrors);

    // Immediately update parent with new values
    if (functionInputs.length > 0) {
      const argsArray = functionInputs.map((input, idx_0) => {
        const val_0 = updatedValues[idx_0] || "";

        // Try to parse based on type
        if (!val_0) return "";

        // For numeric types, keep as string for now (will be processed by workflow)
        if (input.type.startsWith("uint") || input.type.startsWith("int")) {
          return val_0;
        }

        // For boolean
        if (input.type === "bool") {
          return val_0.toLowerCase() === "true";
        }

        // For arrays, try to parse JSON
        if (input.type.includes("[]")) {
          try {
            return JSON.parse(val_0);
          } catch {
            return val_0;
          }
        }

        // Default: return as string
        return val_0;
      });
      onChange(argsArray);
    }
  };
  const handleManualChange = (newValue_0: string) => {
    try {
      const parsed = JSON.parse(newValue_0);
      onChange(parsed);
    } catch {
      // If invalid JSON, store as string
      onChange(newValue_0);
    }
  };

  // Validate input based on ABI type
  const validateInput = (type: string, value_0: string): string | null => {
    if (!value_0 || value_0.trim() === "") {
      return "This field is required";
    }

    // Address validation
    if (type === "address") {
      if (!/^0x[a-fA-F0-9]{40}$/.test(value_0)) {
        return "Invalid address format (must be 0x + 40 hex characters)";
      }
    }

    // Numeric types validation
    if (type.startsWith("uint") || type.startsWith("int")) {
      if (!/^\d+$/.test(value_0) && !/^0x[a-fA-F0-9]+$/.test(value_0)) {
        return "Must be a valid number";
      }
      // Check for uint/int size limits
      const match = type.match(/\d+/);
      if (match) {
        const bits = parseInt(match[0], 10);
        const isUnsigned = type.startsWith("uint");
        const maxValue = isUnsigned ? BigInt(2) ** BigInt(bits) - BigInt(1) : BigInt(2) ** BigInt(bits - 1) - BigInt(1);
        try {
          const numValue = value_0.startsWith("0x") ? BigInt(value_0) : BigInt(value_0);
          if (isUnsigned && numValue < BigInt(0)) {
            return `Must be a positive number for ${type}`;
          }
          if (numValue > maxValue) {
            return `Value exceeds maximum for ${type}`;
          }
        } catch {
          return "Invalid number format";
        }
      }
    }

    // Boolean validation
    if (type === "bool") {
      const lowerValue = value_0.toLowerCase();
      if (lowerValue !== "true" && lowerValue !== "false") {
        return 'Must be "true" or "false"';
      }
    }

    // Bytes validation
    if (type === "bytes" || type.startsWith("bytes")) {
      if (!/^0x[a-fA-F0-9]*$/.test(value_0)) {
        return "Must be valid hex string starting with 0x";
      }
      // For fixed size bytes (e.g., bytes32)
      const match_0 = type.match(/bytes(\d+)/);
      if (match_0) {
        const size = parseInt(match_0[1], 10);
        const expectedLength = size * 2 + 2; // 2 chars per byte + "0x"
        if (value_0.length !== expectedLength) {
          return `Must be exactly ${size} bytes (${expectedLength} characters including 0x)`;
        }
      }
    }

    // Array validation
    if (type.includes("[]")) {
      try {
        const parsed_0 = JSON.parse(value_0);
        if (!Array.isArray(parsed_0)) {
          return "Must be a valid JSON array";
        }
      } catch {
        return "Invalid JSON array format";
      }
    }
    return null;
  };
  const getPlaceholderForType = (type_0: string): string => {
    if (type_0.startsWith("uint") || type_0.startsWith("int")) {
      return "123";
    }
    if (type_0 === "bool") {
      return "true";
    }
    if (type_0 === "address") {
      return "0x...";
    }
    if (type_0.includes("[]")) {
      return '["item1", "item2"]';
    }
    if (type_0 === "bytes" || type_0.startsWith("bytes")) {
      return "0x...";
    }
    return "value";
  };
  const getInputTypeForAbi = (type_1: string): string => {
    if (type_1.startsWith("uint") || type_1.startsWith("int")) {
      return "text"; // Use text to allow large numbers
    }
    return "text";
  };

  // Convert value to JSON string for manual mode
  const jsonValue = React.useMemo(() => {
    return Array.isArray(value) ? JSON.stringify(value, null, 2) : value || "[]";
  }, [value]);

  // Custom validation for JSON array - returns format expected by JsonEditorBase
  const validateJson = (jsonString: string): {
    valid: boolean;
    error?: string;
  } => {
    if (!jsonString || jsonString.trim() === "") {
      if (functionInputs.length > 0) {
        return {
          valid: false,
          error: `${functionInputs.length} argument${functionInputs.length !== 1 ? "s" : ""} required`
        };
      }
      return {
        valid: true
      };
    }
    try {
      const parsed_1 = JSON.parse(jsonString);
      if (!Array.isArray(parsed_1)) {
        return {
          valid: false,
          error: "Arguments must be a JSON array"
        };
      }
      if (functionInputs.length > 0 && parsed_1.length !== functionInputs.length) {
        return {
          valid: false,
          error: `Expected ${functionInputs.length} argument${functionInputs.length !== 1 ? "s" : ""}, got ${parsed_1.length}`
        };
      }
      return {
        valid: true
      };
    } catch (e) {
      return {
        valid: false,
        error: e instanceof Error ? e.message : "Invalid JSON"
      };
    }
  };
  if (functionInputs.length === 0) {
    return <div className="space-y-2">
        {label && <label className="field-label block">{label}</label>}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        <p className="text-ds-text-muted text-sm">This function has no parameters</p>
      </div>;
  }
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between">
          <label className="field-label block">{label}</label>

          {/* Auto/Manual Tabs */}
          <div className="border-ds-border-default bg-ds-surface-card inline-flex rounded-lg border p-0.5">
            <button type="button" onClick={() => setIsManualMode(false)} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${!isManualMode ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-zinc-100"}`}>
              Auto
            </button>
            <button type="button" onClick={() => setIsManualMode(true)} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${isManualMode ? "bg-ds-neutral-200 text-ds-text-primary dark:bg-ds-neutral-700 dark:text-zinc-100" : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-zinc-100"}`}>
              Manual
            </button>
          </div>
        </div>}
      {/* Only show description in manual mode */}
      {description && isManualMode && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      {isManualMode ?
    // Manual Mode: JSON Editor
    <JsonEditorBase label="" description={functionInputs.length > 0 ? `${functionInputs.length} parameter${functionInputs.length !== 1 ? "s" : ""} required: ${functionInputs.map((input_0, idx_1) => `${idx_1 + 1}. ${input_0.name || `arg${idx_1}`} (${input_0.type})`).join(", ")}` : "This function has no parameters"} value={jsonValue} onChange={handleManualChange} placeholder={functionInputs.length > 0 ? `[${functionInputs.map((input_1: any) => `"<${input_1.name || "value"}>"`).join(", ")}]` : "[]"} minHeight="120px" customValidate={validateJson} /> :
    // Auto Mode: Individual Input Fields
    <div className="space-y-3">
          {functionInputs.map((input_2, idx_2) => {
        const hasError = validationErrors[idx_2];
        return <div key={idx_2} className="space-y-1">
                <label className="text-ds-text-secondary block text-xs font-medium">
                  {idx_2 + 1}. {input_2.name || `arg${idx_2}`}
                  <span className="text-ds-text-muted ml-1 font-mono">({input_2.type})</span>
                  <span className="ml-1 text-red-500" title="Required">
                    *
                  </span>
                </label>
                <Input type={getInputTypeForAbi(input_2.type)} value={autoModeValues[idx_2] || ""} onChange={e_0 => handleAutoInputChange(idx_2, e_0.target.value, input_2.type)} placeholder={getPlaceholderForType(input_2.type)} className={`text-ds-text-primary dark:text-white ${hasError ? "border-red-500 focus-visible:ring-red-500/20 dark:border-red-500" : ""}`} required />
                {hasError && <p className="micro-label text-red-500 dark:text-red-400">{hasError}</p>}
              </div>;
      })}
          <p className="label-xs">
            Fill in all {functionInputs.length} required parameter{functionInputs.length !== 1 ? "s" : ""} above
          </p>
        </div>}
    </div>;
}